import { Link } from "react-router-dom";
import { ArrowRight, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { Footer } from "@/components/landing/Footer";
import { BrowserFrame } from "@/components/marketing/BrowserFrame";
import { SeoHead } from "@/components/SeoHead";
import { PRODUCT } from "@/content/solutions";

export default function Product() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Sema",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description: PRODUCT.metaDescription,
    url: "https://semalayer.com/product",
    offers: [
      { "@type": "Offer", name: "Sandbox", price: "0", priceCurrency: "USD", description: "Free forever — 1 connector, 1 user, 100 chat queries/month." },
      { "@type": "Offer", name: "Pro", price: "49", priceCurrency: "USD", description: "$49/user/month (min 3 seats), 14-day free trial. Unlimited connectors, chat, reports, alerts, RBAC + audit log." },
      { "@type": "Offer", name: "Business", price: "99", priceCurrency: "USD", description: "$99/user/month (min 10 seats). Everything in Pro plus SSO and advanced governance." },
    ],
  };

  return (
    <div data-testid="product-root" className="relative min-h-screen w-full bg-background text-foreground">
      <SeoHead title={PRODUCT.metaTitle} description={PRODUCT.metaDescription}
        keywords={PRODUCT.keywords} path="/product" jsonLd={jsonLd} />
      <MarketingNav />

      {/* Hero */}
      <section className="relative overflow-hidden pt-36 pb-16 sm:pt-40">
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-20"
          style={{ background: "radial-gradient(55rem 28rem at 50% -10%, #0FAE96, transparent)" }} />
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-accent">{PRODUCT.eyebrow}</p>
          <h1 data-testid="product-h1" className="mt-5 text-4xl font-semibold tracking-tight sm:text-5xl lg:text-6xl">
            {PRODUCT.h1}
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">{PRODUCT.subtitle}</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/signup">
              <Button size="lg" data-testid="product-cta-signup"
                className="rounded-full bg-accent px-6 font-mono text-xs uppercase tracking-[0.2em] text-accent-foreground hover:bg-accent/90">
                Start free sandbox <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/demo">
              <Button size="lg" variant="outline" className="rounded-full border-border/70 px-6 font-mono text-xs uppercase tracking-[0.2em]">
                Book a walkthrough
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Narrative walkthrough — alternating real screenshots */}
      <section className="py-12 sm:py-16">
        <div className="mx-auto w-full max-w-6xl space-y-24 px-6">
          {PRODUCT.steps.map((s, i) => {
            const flip = i % 2 === 1;
            return (
              <div key={s.key} data-testid={`product-step-${s.key}`}
                className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
                <div className={flip ? "lg:order-2" : ""}>
                  <span className="font-mono text-sm font-semibold tracking-[0.2em] text-accent">{s.n}</span>
                  <h2 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">{s.title}</h2>
                  <p className="mt-4 max-w-md text-base leading-relaxed text-muted-foreground">{s.body}</p>
                </div>
                <div className={flip ? "lg:order-1" : ""}>
                  <BrowserFrame src={s.shot} alt={s.title} caption={s.caption} eager={i === 0} />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Differentiation */}
      <section className="border-t border-border/40 py-24">
        <div className="mx-auto w-full max-w-6xl px-6">
          <div className="max-w-2xl">
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-accent">why it's different</p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">{PRODUCT.differentiation.title}</h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">{PRODUCT.differentiation.subtitle}</p>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
            {PRODUCT.differentiation.columns.map((c) => (
              <div key={c.tag} data-testid={`product-diff-${c.tag.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="flex flex-col rounded-2xl border border-border/60 bg-card/50 p-7 backdrop-blur">
                <h3 className="font-mono text-sm font-semibold uppercase tracking-[0.16em] text-muted-foreground">{c.tag}</h3>
                <div className="mt-5 flex items-start gap-2.5">
                  <X className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" strokeWidth={2.4} />
                  <p className="text-sm leading-relaxed text-muted-foreground">{c.them}</p>
                </div>
                <div className="mt-4 flex items-start gap-2.5 border-t border-border/40 pt-4">
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" strokeWidth={2.4} />
                  <p className="text-sm leading-relaxed text-foreground/90"><span className="font-semibold text-foreground">Sema:</span> {c.sema}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-border/40 py-24">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">Give your data a vocabulary.</h2>
          <p className="mx-auto mt-4 max-w-lg text-base leading-relaxed text-muted-foreground">
            Spin up a sandbox in two minutes against a sample warehouse — or book a 20-minute walkthrough with your own questions.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/signup">
              <Button size="lg" className="rounded-full bg-accent px-6 font-mono text-xs uppercase tracking-[0.2em] text-accent-foreground hover:bg-accent/90">
                Start free sandbox <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/demo">
              <Button size="lg" variant="outline" className="rounded-full border-border/70 px-6 font-mono text-xs uppercase tracking-[0.2em]">
                Book a walkthrough
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
